import React, { useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { fmtCurrency, todayISO, addDays } from "@/lib/utils-app";
import { BedDouble, Calendar, Sparkles } from "lucide-react";

export default function Booking() {
    const [ci, setCi] = useState(todayISO());
    const [co, setCo] = useState(addDays(todayISO(), 2));
    const [guests, setGuests] = useState(2);
    const [roomType, setRoomType] = useState("any");
    const [results, setResults] = useState(null);
    const [open, setOpen] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        language: "en",
        step_free_access: false,
        pillow: "",
    });

    const search = async () => {
        try {
            const { data } = await api.post("/booking/search", {
                check_in: ci,
                check_out: co,
                guests: Number(guests),
                room_type: roomType === "any" ? null : roomType,
            });
            setResults(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Search failed");
        }
    };

    const book = async () => {
        if (!form.name) {
            toast.error("Please provide guest name");
            return;
        }
        try {
            await api.post("/booking/book", {
                room_id: selectedRoom.id,
                check_in: ci,
                check_out: co,
                guest_name: form.name,
                email: form.email || null,
                language: form.language,
                adults: Number(guests),
                accessibility: form.step_free_access ? { step_free_access: true } : {},
                preferences: form.pillow ? { pillow: form.pillow } : {},
            });
            toast.success("Booking confirmed");
            setOpen(false);
            setForm({ name: "", email: "", language: "en", step_free_access: false, pillow: "" });
            search();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Booking failed");
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Direct booking engine"
                title="Book a stay"
                subtitle="Public-style search to capture direct bookings without OTA fees."
            />
            <Card className="mb-6">
                <CardContent className="py-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                        <div>
                            <Label>Check-in</Label>
                            <Input type="date" value={ci} onChange={(e) => setCi(e.target.value)} data-testid="booking-checkin" />
                        </div>
                        <div>
                            <Label>Check-out</Label>
                            <Input type="date" value={co} onChange={(e) => setCo(e.target.value)} data-testid="booking-checkout" />
                        </div>
                        <div>
                            <Label>Guests</Label>
                            <Input type="number" min={1} max={6} value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="booking-guests" />
                        </div>
                        <div>
                            <Label>Room type</Label>
                            <Select value={roomType} onValueChange={setRoomType}>
                                <SelectTrigger data-testid="booking-roomtype">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">Any</SelectItem>
                                    <SelectItem value="Standard">Standard</SelectItem>
                                    <SelectItem value="Deluxe">Deluxe</SelectItem>
                                    <SelectItem value="Suite">Suite</SelectItem>
                                    <SelectItem value="Family">Family</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={search} data-testid="booking-search">
                            <Calendar className="w-4 h-4 mr-1" /> Search
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {results === null && (
                <EmptyState title="Select your dates above" hint="We'll find available rooms in real time." />
            )}
            {results && results.results.length === 0 && (
                <EmptyState title="No rooms available for those dates" hint="Try a different date range or room type." />
            )}

            {results && results.results.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="booking-results">
                    {results.results.map((r) => (
                        <Card key={r.type}>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base">{r.type}</CardTitle>
                                    <span className="text-xs text-muted-foreground capitalize">{r.bed_config} bed</span>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <BedDouble className="w-4 h-4" /> Sleeps {r.capacity} · {r.available_rooms.length} rooms left
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {r.amenities.slice(0, 4).map((a) => (
                                        <span key={a} className="text-[11px] bg-secondary rounded-full px-2 py-0.5">{a}</span>
                                    ))}
                                </div>
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <div className="text-xs text-muted-foreground">Total for {results.nights} night{results.nights > 1 ? "s" : ""}</div>
                                        <div className="text-2xl font-semibold tabular">{fmtCurrency(r.total)}</div>
                                        <div className="text-xs text-muted-foreground">{fmtCurrency(r.rate)} / night</div>
                                    </div>
                                    <Button
                                        onClick={() => {
                                            setSelectedRoom({ id: r.available_rooms[0].id, type: r.type, total: r.total });
                                            setOpen(true);
                                        }}
                                        data-testid="booking-book"
                                    >
                                        <Sparkles className="w-4 h-4 mr-1" /> Book
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Complete your booking</DialogTitle>
                        <DialogDescription>
                            {selectedRoom?.type} · {fmtCurrency(selectedRoom?.total)} total
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Full name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-form-name" />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-form-email" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Language</Label>
                                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                                    <SelectTrigger data-testid="booking-form-language">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="en">English</SelectItem>
                                        <SelectItem value="es">Spanish</SelectItem>
                                        <SelectItem value="fr">French</SelectItem>
                                        <SelectItem value="de">German</SelectItem>
                                        <SelectItem value="pt">Portuguese</SelectItem>
                                        <SelectItem value="ja">Japanese</SelectItem>
                                        <SelectItem value="ar">Arabic</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Pillow preference</Label>
                                <Input value={form.pillow} onChange={(e) => setForm({ ...form, pillow: e.target.value })} placeholder="e.g. memory foam" />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                                checked={form.step_free_access}
                                onCheckedChange={(c) => setForm({ ...form, step_free_access: Boolean(c) })}
                                data-testid="booking-form-accessibility"
                            />
                            <span>Step-free access required (accessibility)</span>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={book} data-testid="booking-form-confirm">Confirm booking</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
